import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";

/* CSS */
import styles from "./CostBreakdownPage.module.css";
import shared from '../styles/shared.module.css';

/* Utils */
import { tripUtils } from "../utils/tripUtils";
import { buildCategoryBreakdown, buildSettlement } from "../utils/costUtils";
import { getCountryImage } from "../utils/countryUtils";
import { formatCurrency } from "../utils/currencyUtils";

/* Icons */
import chevron_down from "../assets/icons/chevron_down.svg";
import default_avatar from "../assets/icons/default_avatar.svg";

const CostBreakdownPage = () => {

    // Pull the trip id out of the URL
    const { id } = useParams();
    const navigate = useNavigate();

    // Names of expanded categories
    const [openCards, setOpenCards] = useState([]);
    const [trip, setTrip] = useState(null);

    const toggleCard = (name) => {
        setOpenCards(prev =>
            prev.includes(name)
                ? prev.filter(n => n !== name)
                : [...prev, name]
        );
    };

    useEffect(() => {
        const fetchTrip = async () => {
            const data = await tripUtils.getTrip(id);
            setTrip(data);
        };
        fetchTrip();
    }, [id]);

    // Loading guard — must come BEFORE we touch `trip`
    if (!trip) return <div>Loading...</div>;

    // Now it's safe
    const breakdown = buildCategoryBreakdown(trip);

    // Get the cover image for the trip
    const firstLocation = trip.locations && trip.locations.length > 0 ? trip.locations[0] : null;
    const coverImage = getCountryImage(firstLocation);

    // How many cards will render? (category buckets + an Uncategorized card if there's untagged money)
    const cardCount = Object.keys(breakdown.buckets).length + (breakdown.uncategorized > 0 ? 1 : 0);

    // Settlement: who paid how much + who owes whom (from the price-split cells)
    const settlement = buildSettlement(trip);
    console.log(settlement);
    const membersById = (trip.members || []).reduce((acc, m) => {
        acc[m.user_id] = { name: m.display_name || m.email, avatar: m.profile_picture_url };
        return acc;
    }, {});
    const memberOf = (uid) => membersById[uid] || { name: uid, avatar: null };
    const netLabel = (net) => net > 0
        ? <><strong>gets back:</strong> {formatCurrency(net)}</>
        : net < 0
            ? <><strong>owes:</strong> {formatCurrency(-net)}</>
            : <strong>settled up</strong>;

    return (
        <div className={styles.page}>
            {/* Page header */}
            <section className={shared.heroHeader}>
                <h1 className={shared.heroTitle}>Cost Breakdown</h1>
            </section>

            {/* Image + button */}
            <div className={styles.heroContainer}>
                <button
                    className={`${shared.heroSubtitle} ${styles.subtitleButton}`}
                    onClick={() => navigate(`/trip/${id}`)}
                >
                    Go to trip: {trip.title}
                </button>
                <img
                    src={coverImage}
                    alt={trip.title}
                    className={styles.heroImage}
                />
            </div>

            {breakdown.hasCategoryColumn ? (
                <div className={`${styles.cardGrid} ${cardCount === 1 ? styles.singleColumn : ''}`}>
                    {Object.entries(breakdown.buckets).map(([name, data]) => {
                        // Which cards are currently expanded?
                        const isOpen = openCards.includes(name);

                        // Card structure
                        return (
                            <div key={name} className={styles.categoryCard}>
                                {/* Category name + total spent in this category */}
                                <div className={styles.cardTop}>
                                    <span className={styles.categoryName}>{name}</span>
                                    <span className={styles.categoryAmount}>
                                        {formatCurrency(data.total)}
                                    </span>
                                </div>
                                {/* Percentage of this category over the whole trip's total */}
                                <span className={styles.categoryPercent}>
                                    {breakdown.total ? Math.round((data.total / breakdown.total) * 100) : 0}%
                                </span>
                                {/* Expanded item list — only rendered when open */}
                                {isOpen && (
                                    <div className={styles.itemList}>
                                        {data.items.map((item, i) => (
                                            <div key={i} className={styles.itemRow}>
                                                <span className={styles.itemName}>{item.place}</span>
                                                <span className={styles.itemPrice}>{formatCurrency(item.price)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {/* Chevron toggle */}
                                <button
                                    className={`${styles.chevronBtn} ${isOpen ? styles.chevronOpen : ''}`}
                                    onClick={() => toggleCard(name)}
                                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                                >
                                    <img src={chevron_down} alt="" />
                                </button>
                            </div>
                        )
                    })}

                    {/* Uncategorized items */}
                    {breakdown.uncategorized > 0 && (
                        <div className={styles.categoryCard}>
                            <div className={styles.cardTop}>
                                <span className={`${styles.categoryName} ${styles.uncategorizedLabel}`}>
                                    Uncategorized
                                </span>
                                <span className={`${styles.categoryAmount} ${styles.uncategorizedAmount}`}>
                                    {formatCurrency(breakdown.uncategorized)}
                                </span>
                            </div>
                            <span className={styles.categoryPercent}>
                                {breakdown.total ? Math.round((breakdown.uncategorized / breakdown.total) * 100) : 0}%
                            </span>
                        </div>
                    )}
                </div>
            ) : (
                <p className={styles.hint}>
                    Add a <strong>Category</strong> column (type <strong>Tags</strong>) to your itinerary to see your spending broken down by category!
                </p>
            )}

            {/* Total Spent */}
            <div className={styles.totalCard}>
                <span className={styles.totalLabel}>Total Spent</span>
                <span className={styles.totalAmount}>
                    {formatCurrency(breakdown.total)}
                </span>
            </div>

            {/* Settlement */}
            <section className={styles.settleSection}>
                <div className={styles.settleHeader}>
                    <h2 className={styles.settleTitle}>Settlement</h2>
                    <p className={styles.settleSubtitle}>Settle these debts to make everyone even</p>
                </div>

                {settlement.hasSplitData ? (
                    <>
                        {/* Who paid how much */}
                        <div className={styles.settleCard}>
                            <h3 className={styles.settleCardTitle}>Current status</h3>
                            {Object.entries(settlement.perPerson)
                                .sort((a, b) => b[1].net - a[1].net)
                                .map(([uid, p]) => {
                                    const m = memberOf(uid);
                                    return (
                                        <div key={uid} className={styles.personRow}>
                                            <div className={styles.personIdentity}>
                                                <img
                                                    src={m.avatar || default_avatar}
                                                    alt={m.name}
                                                    className={styles.personAvatar}
                                                />
                                                <span className={styles.personName}>{m.name}</span>
                                            </div>
                                            <div className={styles.personFigures}>
                                                <span className={styles.personPaid}>
                                                    <strong>paid:</strong> {formatCurrency(p.paid)}
                                                </span>
                                                <span className={styles.figureDot}>•</span>
                                                <span className={
                                                    p.net > 0 ? styles.netPositive
                                                        : p.net < 0 ? styles.netNegative
                                                            : styles.netZero
                                                }>
                                                    {netLabel(p.net)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>

                        {/* Who owes whom */}
                        {settlement.transactions.length > 0 ? (
                            <div className={styles.settleCard}>
                                <h3 className={styles.settleCardTitle}>Payments to be made</h3>
                                {settlement.transactions.map((t, i) => {
                                    const from = memberOf(t.from);
                                    const to = memberOf(t.to);
                                    return (
                                        <div key={i} className={styles.txRow}>
                                            <div className={styles.txParty}>
                                                <img src={from.avatar || default_avatar} alt={from.name} className={styles.personAvatar} />
                                                <span>{from.name}</span>
                                            </div>
                                            <span className={styles.txArrow}>⟶</span>
                                            <div className={styles.txParty}>
                                                <img src={to.avatar || default_avatar} alt={to.name} className={styles.personAvatar} />
                                                <span>{to.name}</span>
                                            </div>
                                            <span className={styles.txAmount}>{formatCurrency(t.amount)}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className={styles.allSettled}>Everyone's all square — nothing to settle 🎉</p>
                        )}

                        {settlement.unassignedCount > 0 && (
                            <p className={styles.unassignedNote}>
                                {settlement.unassignedCount} expense{settlement.unassignedCount > 1 ? 's' : ''} had no payer set and {settlement.unassignedCount > 1 ? 'were' : 'was'} left out of the settlement.
                            </p>
                        )}
                    </>
                ) : (
                    <p className={styles.hint}>
                        Use the <strong>Price Split</strong> column on your itinerary to record who paid for what — then this shows who owes whom.
                    </p>
                )}
            </section>
        </div>
    );
};

export default CostBreakdownPage;
